import { Spinner, Alert, Button, Form, ActionGroup } from '@patternfly/react-core';
import React, { ReactElement, useState } from 'react';
import './LoginPage.scss';
import CenteredPage from '../../../layout/CenteredPage';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMount } from 'react-use';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import Loader from '../../../components/util/Loader';
import { OtpInput } from './OtpInput';
import { LoginMFA as MFA_Login, ResendMFA } from '../../../api/login/Login';
import { getCSRFToken } from '../../../api/security/CSRFToken';
import axiosConfig from '../../../api/axiosConfig';
import { AxiosError } from 'axios';

interface Props {
	isSSO?: boolean;
}

export default function LoginMFA(props: Props): ReactElement {
	const [otp, setOtp] = useState('');
	const [loading, setLoading] = useState(true);
	const [btnLoading, setBtnLoading] = useState(false);
	const [message, setMessage] = useState<React.ReactElement | null>();
	const navigate = useNavigate();
	const location = useLocation();
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	const mfaToken = location.state?.mfa_token as string;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	const userEmail = location.state?.email as string;

	useMount(() => {
		void getCSRFToken().then((token) => {
			axiosConfig.instance.defaults.headers['CSRF-TOKEN'] = token;
		});

		setTimeout(() => {
			setLoading(false);
		}, 1250);
	});

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			void verifyOtp();
		}
	};

	const verifyOtp = () => {
		setBtnLoading(true);

		if (otp === '' || otp === '') {
			setMessage(
				<Alert
					variant="danger"
					isInline
					isPlain
					title="Verification Code must be set"
				/>
			);
			setLoading(false);
			setBtnLoading(false);
			return;
		}

		if (otp && otp.length != 6) {
			setMessage(
				<Alert
					variant="danger"
					isInline
					isPlain
					title="Verification Code must be exactly 6 digits"
				/>
			);

			setLoading(false);
			setBtnLoading(false);
			return;
		}

		void MFA_Login(mfaToken, otp)
			.then((response) => {
				if (response.pinnedDashboardId) {
					navigate(`/analyze/dashboards/view/${response.pinnedDashboardId}`);
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				} else if (location.state && location.state.nextPath) {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
					navigate(location.state.nextPath);
				} else {
					navigate('/getting-started');
				}
			})
			.catch((error: AxiosError) => {
				console.error('login MFA error', error);
				setMessage(
					<Alert
						variant="danger"
						isInline
						isPlain
						title="Something went wrong submitting the OTP, Check that you entered the OTP correctly"
					/>
				);

				setLoading(false);
				setBtnLoading(false);
			});
	};

	const resendOtp = () => {
		void ResendMFA(mfaToken).then((response) => {
			setMessage(
				<Alert
					variant="success"
					isInline
					isPlain
					title="OTP request has succesfully been re-sent"
				/>
			);
		});
	};

	const verifyButton = loading ? (
		<div className="spinner-container">
			<Spinner size="lg" />
		</div>
	) : btnLoading ? (
		<Loader />
	) : (
		<Button
			onClick={verifyOtp}
			variant="primary"
			data-testid="mfa-submit"
			type="button"
			className="btn-login"
		>
			Verify Code
		</Button>
	);

	return (
		<CenteredPage
			description={
				loading
					? ''
					: `We have sent a 6-digit verification code to your email address ${
							userEmail ? `(${userEmail})` : ''
					  }. Please enter it below to continue.`
			}
			notice={message}
		>
			{loading && (
				<div className="spinner-container">
					<Spinner size="lg" />
					<p>Sending verification code…</p>
				</div>
			)}

			{!loading && (
				<Form autoComplete="one-time-code">
					<OtpInput onChange={(otp) => setOtp(otp)} />
					<ActionGroup className="actiongroup-center">{verifyButton}</ActionGroup>
					<br />
					<div className="text-center">
						<Button
							className="text-center"
							variant="link"
							isInline
							onClick={() => {
								navigate('/login');
							}}
							data-testid="mfa-back"
						>
							<FontAwesomeIcon icon={faArrowLeft} /> Go back
						</Button>
						<br />
						<Button
							variant="link"
							isInline
							onClick={resendOtp}
							data-testid="mfa-resend"
						>
							Didn't receive the code? Resend
						</Button>
					</div>
				</Form>
			)}
		</CenteredPage>
	);
}
